import { inject, Injector, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxComponentInputs,
  OnyxDropdownOptionsSourceResult,
  OnyxFilter,
  OnyxHttpErrorResponse,
  OnyxOption,
  OnyxPaginated,
  OnyxPagination,
} from '@onyx/angular';
import {
  AddressHelper,
  ONYX_TOASTS_LIMIT,
  OnyxPhonePipe,
  OnyxToastService,
} from '@onyx/angular';
import { chain, isString, omit } from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  map,
  merge,
  mergeMap,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
  zip,
} from 'rxjs';
import { IconTextTooltipComponent } from '../../../../../common/components/tooltips/icon-text-tooltip/icon-text-tooltip.component';
import { CONFLICT_ERROR_CODE } from '../../../../../common/constants/common/conflict-error-code';
import { VALIDATION_ERROR_CODES } from '../../../../../common/constants/common/validation-error-codes';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { FilterHelper } from '../../../../../common/helpers/filter.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import type { BatchFileUpload } from '../../../../../common/interfaces/utilities/batch-file-upload';
import { ApiService } from '../../../../../common/services/api.service';
import { StorageService } from '../../../../../common/services/storage.service';
import type { ImportProgress } from '../../../../common/interfaces/import-progress';
import type { ContractorBranchForm } from '../../contractor-form/contractor-branches-form/contractor-branch-form-modal/contractor-branch-form-modal.component';
import type { BlockContractorModalForm } from '../components/block-contractor-modal/block-contractor-modal.component';
import type { ContractorsImportData } from '../constants/contractors-import-schema';
import { ContractorStatus } from '../enums/contractor-status';
import type { ContractorFormDto } from '../helpers/contractor-form.helper';
import type { ContractorSort } from '../enums/contractor-sort';
import type { Contractor } from '../interfaces/contractor';
import type { ContractorBranch } from '../interfaces/contractor-branch';

export interface ContractorsFilters {
  filters?: OnyxFilter[];
  query?: string;
  sort?: ContractorSort;
}

@Service()
export class ContractorsService extends ApiService {
  private readonly storageService = inject(StorageService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly phonePipe = inject(OnyxPhonePipe);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listContractors(
    params: OnyxPagination & ContractorsFilters,
  ): Observable<OnyxPaginated<Contractor>> {
    return this.get('/contractor', {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.filters && FilterHelper.buildQueryParams(params.filters)),
        ...(params.query && { query: params.query }),
        ...(params.sort && { 'sort[]': params.sort }),
      },
    });
  }

  public searchContractors(
    query: string,
    params: Omit<OnyxPagination & ContractorsFilters, 'query'>,
  ): Observable<OnyxDropdownOptionsSourceResult<Contractor>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.listContractors({ ...params, query })),
      map((response) => ({
        options: chain(response.items)
          .map((contractor): OnyxOption<Contractor> => ({
            name: contractor.companyProfile.displayName,
            value: contractor,
            description: `VAT ID: ${contractor.companyProfile.vatId}`,
            hint: contractor.companyProfile.types
              ?.map(({ value }) =>
                this.translateService.instant(
                  `${DictionaryCode.CONTRACTOR_TYPE}.${value}`,
                ),
              )
              .join(' · '),
            leftFlag: contractor.companyProfile.country,
            ...(contractor.block && {
              rightIcon: { name: 'tooltip-filled', size: 16 },
              rightIconTooltip: {
                ref: IconTextTooltipComponent,
                inputs: {
                  heading: `${DictionaryCode.CONTRACTOR_BLOCK_REASON}.${contractor.block.reason.value}`,
                  description: contractor.block.reasonDescription,
                } satisfies OnyxComponentInputs<IconTextTooltipComponent>,
              },
              disabled: true,
            }),
          }))
          .groupBy((option) => option.disabled)
          .entries()
          .map(([disabled, options]) => ({
            id: disabled === 'false' ? 'active' : 'disabled',
            subheading: disabled === 'true' ? 'labels.blocked' : undefined,
            options,
          }))
          .filter(({ options }) => !!options.length)
          .orderBy(({ options }) => !!options[0].disabled)
          .value(),
        totalItems: response.totalItems,
      })),
    );
  }

  public getContractor(uuid: string): Observable<Contractor> {
    return this.get(`/contractor/${uuid}`);
  }

  public addContractor(dto: ContractorFormDto): Observable<void> {
    const { branches, ...contractorDto } = dto;
    const addBranches$ = from(branches).pipe(
      concatMap((branch) => this.addBranch(dto.uuid, branch)),
      defaultIfEmpty(undefined),
    );

    return this.uploadFiles(contractorDto).pipe(
      switchMap((contractorDto) =>
        this.post<void>('/contractor', contractorDto),
      ),
      switchMap((contractor) => zip(of(contractor), addBranches$)),
      map(([contractor]) => contractor),
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public importContractors(
    data: ContractorsImportData[],
  ): Observable<ImportProgress> {
    const dto = data
      .map((item) => omit(item, 'index'))
      .map(({ assignedPointsOfInterest, ...item }): ContractorFormDto => ({
        ...item,
        pointsOfInterest: assignedPointsOfInterest.map((pointOfInterest) => ({
          uuid: pointOfInterest.uuid,
          gate: null,
        })),
      }));

    return from(dto).pipe(
      concatMap((dto, index) =>
        this.addContractor(dto).pipe(
          map(() => ({ ok: true, index })),
          defaultIfEmpty({ ok: false, index }),
          catchError((response) => {
            ValidationHelper.handleUnexpectedResponse(response, this.injector);
            return of({ ok: false, index });
          }),
        ),
      ),
    );
  }

  public editContractor(
    contractor: Contractor,
    dto: ContractorFormDto,
  ): Observable<void> {
    const previousBranchesUuid = new Set(
      contractor.branches.map((branch) => branch.uuid),
    );
    const { branches, ...contractorDto } = dto;

    const addBranches$ = from(
      branches.filter(({ uuid }) => !previousBranchesUuid.has(uuid)),
    ).pipe(concatMap((branch) => this.addBranch(contractor.uuid, branch)));
    const editBranches$ = from(
      branches.filter(({ uuid }) => previousBranchesUuid.has(uuid)),
    ).pipe(mergeMap((branch) => this.editBranch(contractor.uuid, branch)));
    const deleteBranches$ = from(
      [...previousBranchesUuid.values()].filter(
        (uuid) => !branches.some((branch) => branch.uuid === uuid),
      ),
    ).pipe(mergeMap((uuid) => this.deleteBranch(contractor.uuid, uuid)));

    const updateBranches$ = merge(
      addBranches$,
      editBranches$,
      deleteBranches$,
    ).pipe(defaultIfEmpty(undefined));

    return updateBranches$.pipe(
      switchMap(() => this.uploadFiles(contractorDto)),
      switchMap((contractorDto) =>
        this.put<void>(`/contractor/${contractor.uuid}`, contractorDto),
      ),
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public getBranches(
    contractorUuid: string | Contractor,
  ): Observable<OnyxOption<string>[]> {
    const contractor$ = isString(contractorUuid)
      ? this.getContractor(contractorUuid)
      : of(contractorUuid);

    const buildOption = (
      branch: Contractor | ContractorBranch,
    ): OnyxOption<string> => {
      const { mainAddress } = branch.addresses;
      return {
        name: 'name' in branch ? branch.name : 'labels.mainBranch',
        value: branch.uuid,
        leftFlag: mainAddress.countryCode,
        description: AddressHelper.composeLabel(mainAddress),
      };
    };

    return contractor$.pipe(
      map((contractor) => [
        buildOption(contractor),
        ...contractor.branches.map((branch) => buildOption(branch)),
      ]),
    );
  }

  public addBranch(
    contractorUuid: string,
    branch: ContractorBranchForm,
  ): Observable<void> {
    return this.post<void>(
      `/contractor/${contractorUuid}/branches`,
      branch,
    ).pipe(tap(() => this._reload$.next()));
  }

  public editBranch(
    contractorUuid: string,
    branch: ContractorBranchForm,
  ): Observable<void> {
    return this.put<void>(
      `/contractor/${contractorUuid}/branches/${branch.uuid}`,
      branch,
    ).pipe(tap(() => this._reload$.next()));
  }

  public deleteBranch(
    contractorUuid: string,
    branchUuid: string,
  ): Observable<void> {
    return this.delete<void>(
      `/contractor/${contractorUuid}/branches/${branchUuid}`,
    ).pipe(tap(() => this._reload$.next()));
  }

  public blockContractor(
    uuid: string,
    form: BlockContractorModalForm,
  ): Observable<void> {
    return this.put<void>(`/contractor/${uuid}/status`, {
      status: ContractorStatus.BLOCKED,
      block: form,
    }).pipe(tap(() => this._reload$.next()));
  }

  public unblockContractor(uuid: string): Observable<void> {
    return this.put<void>(`/contractor/${uuid}/status`, {
      status: ContractorStatus.ACTIVE,
      block: null,
    }).pipe(tap(() => this._reload$.next()));
  }

  public archiveContractor(uuid: string): Observable<void> {
    return this.post<void>(`/contractor/${uuid}/archive`, undefined).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public unarchiveContractor(uuid: string): Observable<void> {
    return this.post<void>(`/contractor/${uuid}/unarchive`, undefined).pipe(
      tap(() => this._reload$.next()),
    );
  }

  private uploadFiles(
    dto: Omit<ContractorFormDto, 'branches'>,
  ): Observable<Omit<ContractorFormDto, 'branches'>> {
    return of(dto).pipe(
      map((dto): BatchFileUpload[] => [
        ...dto.documents.map((document, index) => ({
          path: `documents[${index}].scan`,
          files: document.scan,
        })),
      ]),
      switchMap((data) => this.storageService.uploadBatch(data)),
      map((data) => this.storageService.mergeBatch(dto, data)),
    );
  }

  private handleConflictErrors(
    dto: ContractorFormDto,
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    if (
      response.error.companyProfile?.vatId ===
      'Value must be unique per country'
    ) {
      const { country, vatId } = dto.companyProfile;
      this.toastService.showError({
        message: this.translateService.instant(
          'contractors.toasts.conflictErrors.vatId',
          { country: country.toUpperCase(), vatId },
        ),
      });
      response.handled = true;
    }

    if (response.error.companyProfile?.displayName === CONFLICT_ERROR_CODE) {
      this.toastService.showError({
        message: this.translateService.instant(
          'contractors.toasts.conflictErrors.displayName',
          { displayName: dto.companyProfile.displayName },
        ),
      });
      response.handled = true;
    }

    const CONFLICT_KEYS = ['email', 'phone', 'timocomId', 'transId'] as const;

    const errors = response.error.contactPersons as Record<
      `${number}`,
      Record<(typeof CONFLICT_KEYS)[number], string | undefined>
    >;
    const conflictErrors = chain(CONFLICT_KEYS)
      .filter((key) =>
        Object.values(errors).some(
          (value) => value[key] === CONFLICT_ERROR_CODE,
        ),
      )
      .slice(0, ONYX_TOASTS_LIMIT)
      .value();

    for (const field of conflictErrors) {
      const errorValues = chain(errors)
        .keys()
        .map((key) => dto.contactPersons[+key][field])
        .map((value) =>
          isString(value) ? value : this.phonePipe.transform(value),
        )
        .join(', ')
        .value();

      this.toastService.showError({
        message: this.translateService.instant(
          `contractors.toasts.conflictErrors.${`${field}Exists`}`,
          { [field]: errorValues },
        ),
      });
    }

    if (conflictErrors.length) response.handled = true;

    throw response;
  }
}
